import { useLayoutEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { useUser } from "@/lib/user-context";

function RecoveryDialog() {
  const { retryIdentity, logout, isCheckingIdentity, isLoggingOut, identityRecoveryError } = useUser();
  const busy = isCheckingIdentity || isLoggingOut;

  useLayoutEffect(() => {
    // Conceal every private root, including Radix portals, without unmounting
    // their React state. Use the existing modal stack so recovery interactions
    // cannot dismiss or refocus a concealed dialog that owns an unsent draft.
    document.body.setAttribute("data-identity-recovery", "held");
    return () => {
      document.body.removeAttribute("data-identity-recovery");
    };
  }, []);

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay data-identity-recovery-root="" className="fixed inset-0 z-[100] bg-black/80" />
        <Dialog.Content
          data-identity-recovery-root=""
          data-testid="identity-recovery"
          aria-busy={busy}
          onEscapeKeyDown={event => event.preventDefault()}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
          className="fixed left-1/2 top-1/2 z-[100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 text-foreground shadow-2xl"
        >
          <Dialog.Title className="text-xl font-semibold">Confirm your account</Dialog.Title>
          <Dialog.Description className="mt-3 text-sm text-muted-foreground">
            Private screens are hidden until we confirm which account is active. Your work is kept while we check.
          </Dialog.Description>
          <p className="mt-3 text-sm" role="status">
            {isLoggingOut ? "Signing out…" : isCheckingIdentity ? "Checking your account…" : identityRecoveryError}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button disabled={busy} onClick={() => void retryIdentity()}>Retry identity check</Button>
            <Button variant="outline" disabled={busy} onClick={() => void logout()}>Sign out</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function IdentityRecovery() {
  const { isIdentityUnconfirmed } = useUser();
  return isIdentityUnconfirmed ? <RecoveryDialog /> : null;
}
